import { useState } from 'react';
import { HbsModal } from './hbs/Modal';
import { HbsButton } from './hbs/Button';
import { HbsInput } from './hbs/Input';
import { EyeToggle } from './hbs/EyeToggle';
import { useSafe } from '../context/SafeContext';
import { vaultItemsToLastpassCsv } from '../lib/exportLastpass';
import { encryptSafeExport, passkeyCount, SAFE_EXPORT_EXTENSION } from '@heybesafe/shared';

interface ExportSafeModalProps {
  open: boolean;
  onClose: () => void;
}

/** CSV = logins-only interop (LastPass format); backup = full encrypted safe. */
type Format = 'csv' | 'backup';

type State =
  | { kind: 'idle'; error: string | null }
  | { kind: 'verifying' }
  | { kind: 'downloading' }
  | { kind: 'done'; count: number; format: Format };

function todayLocalISODate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function triggerDownload(contents: string, filename: string, mime: string): void {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ExportSafeModal({ open, onClose }: ExportSafeModalProps): JSX.Element | null {
  const { verifyPassword, items } = useSafe();
  const [format, setFormat] = useState<Format>('backup');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [state, setState] = useState<State>({ kind: 'idle', error: null });

  // A backup captures the caller's OWN safe only — org/shared items belong to
  // the family, not this personal export.
  const personalItems = items.filter((i) => !i.orgId);

  function reset(): void {
    setPassword('');
    setReveal(false);
    setState({ kind: 'idle', error: null });
  }

  function handleClose(): void {
    if (state.kind === 'verifying' || state.kind === 'downloading') return;
    reset();
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    if (password.length === 0) return;
    setState({ kind: 'verifying' });
    const ok = await verifyPassword(password);
    if (!ok) {
      setPassword('');
      setState({ kind: 'idle', error: "That's not your safe password." });
      return;
    }

    setState({ kind: 'downloading' });
    try {
      if (format === 'csv') {
        const { csv, count } = vaultItemsToLastpassCsv(items);
        triggerDownload(csv, `heybesafe-logins-${todayLocalISODate()}.csv`, 'text/csv;charset=utf-8');
        setPassword('');
        setState({ kind: 'done', count, format });
      } else {
        // The safe password doubles as the backup passphrase — the user just
        // proved they know it, and reusing it avoids a second secret to lose.
        const file = await encryptSafeExport(personalItems, password, new Date().toISOString());
        triggerDownload(
          file,
          `heybesafe-backup-${todayLocalISODate()}.${SAFE_EXPORT_EXTENSION}`,
          'application/json',
        );
        setPassword('');
        setState({ kind: 'done', count: personalItems.length, format });
      }
    } catch {
      setPassword('');
      setState({ kind: 'idle', error: 'Something went wrong preparing the export. Please try again.' });
    }
  }

  if (!open) return null;

  const busy = state.kind === 'verifying' || state.kind === 'downloading';
  const canSubmit = password.length > 0 && !busy;
  const passkeys = passkeyCount(personalItems);

  return (
    <HbsModal
      open={open}
      onClose={handleClose}
      title="Export your safe"
      size="md"
      dismissOnBackdrop={!busy}
      dismissOnEsc={!busy}
    >
      {state.kind === 'done' ? (
        <>
          {state.format === 'backup' ? (
            <p>Backed up {state.count} {state.count === 1 ? 'item' : 'items'}.</p>
          ) : (
            <p>Exported {state.count} {state.count === 1 ? 'login' : 'logins'}.</p>
          )}
          <p className="text-hbs-text-mute mt-2">
            {state.format === 'backup'
              ? 'The encrypted backup is in your Downloads folder. You’ll need this same safe password to restore it — keep both somewhere safe.'
              : 'The file is in your Downloads folder. Treat it like cash — store it somewhere safe and delete it when you’re done.'}
          </p>
          <div className="mt-6 flex justify-end">
            <HbsButton id="export-close-button" onClick={handleClose}>Close</HbsButton>
          </div>
        </>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void handleSubmit(); }} className="space-y-3">
          {/* Format picker */}
          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio" name="export-format" className="mt-1"
                checked={format === 'backup'} onChange={() => setFormat('backup')}
                disabled={busy}
              />
              <span>
                <span className="font-medium">Full encrypted backup</span>
                <span className="block text-hbs-text-mute text-[13px]">
                  Everything in your safe — logins, notes, cards, {passkeys > 0 ? `${passkeys} passkey${passkeys === 1 ? '' : 's'}, ` : 'passkeys, '}
                  and more — encrypted with your safe password. Use this to move or restore your whole safe.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio" name="export-format" className="mt-1"
                checked={format === 'csv'} onChange={() => setFormat('csv')}
                disabled={busy}
              />
              <span>
                <span className="font-medium">Logins for another app (CSV)</span>
                <span className="block text-hbs-text-mute text-[13px]">
                  A plain-text spreadsheet of your logins only, for importing into another password
                  manager. No passkeys. Treat it like cash.
                </span>
              </span>
            </label>
          </div>

          <p className="text-hbs-text-mute text-[13px]">
            {format === 'backup'
              ? 'Confirm your safe password to encrypt the backup.'
              : 'This file will contain your passwords in plain text.'}
          </p>
          <HbsInput
            type={reveal ? 'text' : 'password'}
            label="Safe password"
            value={password}
            onChange={setPassword}
            mono={reveal}
            autoFocus
            trailingActions={<EyeToggle revealed={reveal} onChange={setReveal} />}
          />
          {state.kind === 'idle' && state.error && (
            <p role="alert" className="text-hbs-danger text-[13px]">{state.error}</p>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <HbsButton type="button" variant="ghost" id="export-cancel-button" onClick={handleClose} disabled={busy}>
              Cancel
            </HbsButton>
            <HbsButton id="export-submit-button" type="submit" disabled={!canSubmit} loading={busy}>
              Export
            </HbsButton>
          </div>
        </form>
      )}
    </HbsModal>
  );
}
